import React, { FC, useCallback, useEffect, useState } from "react";
import {
  DataGrid,
  GridColDef,
  GridSortModel,
  GridSortItem,
  GridRowModesModel,
} from "@mui/x-data-grid";
import { Container } from "@mui/material";
import { useSnackbar } from "notistack";
import { SecLevel } from "../../types/seclevel";
import { SecLevelEndpoint, SecLevelsPortal } from "../../api/portal";
import { makeColumn } from "../../util/tableUtils";

const columns = [
  { field: "id", headerName: "Tabelle", type: "number", width: 100 },
  { field: "layername", headerName: "Layer", width: 250 },
  {
    field: "sec_level",
    headerName: "Security Level",
    type: "number",
    width: 200,
  },
] as const;

/**
 * Endpoint needs to be dynamic because it's different between
 * portal and webmaps (but works exactly the same way across both, unlike Accounts/Webmaps that have
 * a different logic in the portal than in the webmap backends)
 */
interface SecLevelsTableProps {
  endpoint: SecLevelEndpoint;
}

const SecLevelsTable: FC<SecLevelsTableProps> = ({
  endpoint = SecLevelsPortal,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [orderByModel, setOrderByModel] = useState<GridSortItem[]>([
    {
      field: "id",
      sort: "asc",
    },
  ]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [secLevelData, setSecLevelData] = useState<SecLevel[]>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  const handleUpdate = (row: SecLevel) => {
    setIsLoading(true);
    endpoint
      .update(row.id, row) // update row data
      .then(() => {
        enqueueSnackbar(`Layer ${row.id} aktualisiert`, {
          key: `row-updated-${row.id}`,
        });
      })
      .catch(() => {
        enqueueSnackbar(`Aktualisierung von Layer ${row.id} fehlgeschlagen`, {
          key: `row-update-failed-${row.id}`,
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchSecLevels = useCallback(() => {
    const { page, pageSize } = paginationModel;
    endpoint
      .getAll(
        orderByModel[0]?.field, // community edition only supports single sort
        orderByModel[0]?.sort,
        pageSize,
        page * pageSize
      )
      .then((res) => {
        // we use the x-total-count header for retrieving the total number of resources
        setTotalRowCount(Number(res.headers["x-total-count"]));
        return res.data;
      })
      .then((secLevels: SecLevel[]) => {
        setSecLevelData(secLevels);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [orderByModel, paginationModel]);

  useEffect(() => {
    setIsLoading(true);
    fetchSecLevels();
  }, [orderByModel, paginationModel, fetchSecLevels]);

  return (
    <Container
      style={{
        width:
          columns.map((c) => c.width).reduce((prev, c) => prev + c, 0) * 1.1,
      }}
      sx={{ m: 0, p: 0, height: "calc(100vh - 157px)" }}
      maxWidth={false}
      disableGutters
    >
      <DataGrid
        rows={secLevelData}
        columns={columns.map((col) => {
          return makeColumn(col);
        })}
        rowModesModel={rowModesModel}
        onRowModesModelChange={(model) => {
          setRowModesModel(model);
        }}
        pageSizeOptions={[10, 20, 50]}
        editMode="row"
        processRowUpdate={(row: SecLevel) => {
          handleUpdate(row);
          return row;
        }}
        onProcessRowUpdateError={() => {}}
        paginationModel={paginationModel}
        onPaginationModelChange={(model) => {
          setPaginationModel(model);
        }}
        sortingMode="server"
        sortModel={orderByModel}
        onSortModelChange={(model: GridSortModel) => {
          setOrderByModel(model);
        }}
        paginationMode="server"
        rowCount={totalRowCount}
        loading={isLoading}
      />
    </Container>
  );
};

export default SecLevelsTable;
